import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { motion } from "framer-motion";
import { FileText } from "lucide-react";
import Footer from "./Footer";
import NavBar from "./NavBar";
import Loading from "./Loading";
import api, { authHeaders } from "../services/api";

function anoAtual() {
  return new Date().getFullYear();
}

function anosDisponiveis() {
  const actual = anoAtual();
  return Array.from({ length: 8 }, (_, index) => actual - index);
}

function dataRecibo(data) {
  const dataTransacao = new Date(data);
  if (Number.isNaN(dataTransacao.getTime())) return "Sem data";

  const dia = String(dataTransacao.getDate()).padStart(2, "0");
  const mes = String(dataTransacao.getMonth() + 1).padStart(2, "0");
  const ano = dataTransacao.getFullYear();

  return `${dia}/${mes}/${ano}`;
}

export default function ReciboUsuarioCliente() {
  const [recibos, setRecibos] = useState([]);
  const [usuario, setUsuario] = useState("");
  const [naoHaRecibos, setNaoHaRecibos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [anoSelecionado, setAnoSelecionado] = useState(anoAtual());
  const navigate = useNavigate();

  const mostrarRecibos = useCallback(async (ano) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setRecibos([]);
      setNaoHaRecibos(true);
      return;
    }

    try {
      setLoading(true);
      const dadosDoLocalStorage = jwtDecode(token);
      const headers = authHeaders(token);
      const dadosUsuario = await api.get(
        `/usuario/${dadosDoLocalStorage.idUsuario}`,
        { headers }
      );
      const dadosRecibo = await api.get(
        `/recibos/${dadosUsuario.data.idUsuario}?ano=${ano}`,
        { headers }
      );

      const listaRecibos = Array.isArray(dadosRecibo.data)
        ? dadosRecibo.data
        : [];

      setUsuario(dadosUsuario.data?.name || "");
      setRecibos(listaRecibos);
      setNaoHaRecibos(listaRecibos.length === 0);
    } catch (erro) {
      console.log(erro);
      setRecibos([]);
      setNaoHaRecibos(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    mostrarRecibos(anoSelecionado);
  }, [anoSelecionado, mostrarRecibos]);

  return (
    <div className="flex min-h-screen flex-col justify-between">
      <div className="sticky top-0 z-10 bg-blue-500">
        <NavBar />
      </div>

      {loading ? (
        <Loading />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto my-8 h-auto w-full max-w-6xl rounded-xl border px-4 sm:px-8"
        >
          <div className="m-4 flex flex-col">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-center text-lg text-gray-600 sm:text-left sm:text-xl">
                Historico de pagamento do usuario{" "}
                <span className="font-semibold uppercase text-gray-900">
                  {usuario}
                </span>
              </h2>
              <label className="text-sm font-semibold text-gray-600">
                Ano
                <select
                  value={anoSelecionado}
                  onChange={(e) => setAnoSelecionado(Number(e.target.value))}
                  className="ml-2 rounded-md border px-3 py-2"
                >
                  {anosDisponiveis().map((ano) => (
                    <option key={ano} value={ano}>
                      {ano}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {naoHaRecibos ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                Nenhum recibo encontrado para {anoSelecionado}.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 text-sm sm:text-base">
                  <thead className="bg-gray-100">
                    <tr className="text-gray-500">
                      <th className="border px-2 py-2 text-center">ID</th>
                      <th className="border px-2 py-2 text-center">Data</th>
                      <th className="border px-2 py-2 text-center">
                        Metodo de Pagamento
                      </th>
                      <th className="border px-2 py-2 text-center">Montante</th>
                      <th className="border px-2 py-2 text-center">Recibo</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    {recibos.map((recibo, i) => (
                      <motion.tr
                        key={recibo.idRecibo || i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.03 }}
                        className="border"
                      >
                        <td className="border px-2 py-2 text-center">{i + 1}</td>
                        <td className="border px-2 py-2 text-center">
                          {dataRecibo(recibo.dataTransacao)}
                        </td>
                        <td className="border px-2 py-2 text-center">
                          {recibo.meioPagamento}
                        </td>
                        <td className="border px-2 py-2 text-center">
                          {recibo.valor}
                        </td>
                        <td className="border px-2 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => navigate(`/recibo/${recibo.idRecibo}`)}
                            className="mx-auto inline-flex items-center justify-center text-blue-600 hover:text-blue-500"
                            title="Abrir recibo"
                          >
                            <FileText size={20} />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      )}

      <div className="mt-auto bg-blue-500">
        <Footer />
      </div>
    </div>
  );
}
